/**
 * @file useLoginUser.js
 * @fileoverview This file contains the custom hook for handling user login functionality.
 * It includes methods for OTP login, password login, and managing the login state.
 * It uses Redux for state management and Formik for form handling.
 */

import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    checkEmailExists,
    loginWithEmailAndPassword,
    sentOtpToEmail,
    verifyOtp,
    resendOtp,
    setEmail
} from '../../../../app/store/slices/auth/authSlice';
import { trimAndCleanSpaces } from '../../../../utils/trimAndCleanSpaces';

export default function useLoginUser() {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [activeLoginMethod, setActiveLoginMethod] = useState('login');
    const [resendLoading, setResendLoading] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [isTooManyRequests, setIsTooManyRequests] = useState(false);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: () => {}
    });

    // to run timer in resend otp email
    useEffect(() => {
        const timer = setInterval(() => {
            if (seconds > 0) {
                setSeconds(seconds - 1);
            }

            if (seconds === 0) {
                clearInterval(timer);
            }
        }, 1000);

        // clear the Interval function from browser
        return () => {
            clearInterval(timer);
        };
    }, [seconds]);

    async function handleOtpLogin() {
        try {
            // set isOtpLogin true in formik state
            formik.setFieldValue('isOtpLogin', true);

            // if email is not provided then set error message in email input field
            if (!formik.values.email) {
                formik.setFieldError('email', 'Email is required');
                formik.setSubmitting(false);
                return;
            }

            // clean the email and convert to lowercase
            const cleanEmail = trimAndCleanSpaces(formik.values.email).toLowerCase();

            // make a api call to check the email exists or not
            const emailCheckResult = await dispatch(checkEmailExists(cleanEmail));

            // if email check result api is rejected then set error message in email input field
            if (emailCheckResult?.error?.message === 'Rejected') {
                formik.setFieldError('email', 'Something went wrong! Please try again.');
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
                return;
            }

            // if email does not exist then set error message in email input field
            if (!emailCheckResult?.payload?.exist) {
                formik.setFieldError('email', 'Email does not exist. Please sign up.');
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
                return;
            }

            // if email exists then make a api call to send otp to email
            const sentOtpResult = await dispatch(sentOtpToEmail(cleanEmail));

            // console.log("Sent OTP Result: ", sentOtpResult);

            // if sent otp api is rejected then set error message in email input field
            if (sentOtpResult?.error?.message === 'Rejected') {
                formik.setFieldError('email', sentOtpResult.payload);
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
                return;
            }

            // if sent otp api is successful then set active login method to otpLogin and set seconds to 60 and also update the email state globally
            if (sentOtpResult?.payload?.status) {
                setActiveLoginMethod('otpLogin');
                setSeconds(60);
                setIsTooManyRequests(false);
                formik.setFieldValue('otp', '');
                dispatch(setEmail(cleanEmail));
                formik.setSubmitting(false);
                return;
            }
        } catch (error) {
            // handle error part
            formik.setFieldError('email', 'Failed to send magic link. Please try again.');
            formik.setSubmitting(false);
            formik.setFieldTouched('email', true, false);
        }
    }

    async function loginWithOtp() {
        try {
            // clean the email and store otp in variable
            const cleanEmail = trimAndCleanSpaces(formik.values.email).toLowerCase();
            const otp = formik.values.otp;

            // make a api call to verify otp with email and otp
            const response = await dispatch(verifyOtp({ email: cleanEmail, otp }));

            // if response is rejected then set error message in otp input field
            if (response?.error?.message === 'Rejected') {
                formik.setFieldError('otp', response.payload);
                formik.setSubmitting(false);
                formik.setFieldTouched('otp', true, false);
                if (response?.payload === 'Too Many Requests') {
                    setIsTooManyRequests(true);
                }
                return;
            }

            formik.resetForm(); // reset the form after successful login
            setIsTooManyRequests(false);
        } catch (error) {
            // handle error part
            formik.setFieldError('otp', 'Failed to verify OTP. Please try again.');
            formik.setSubmitting(false);
            formik.setFieldTouched('otp', true, false);
        }
    }

    async function resendOtpEmail() {
        try {
            // set resend loading to true and clean email
            setResendLoading(true);
            const cleanEmail = trimAndCleanSpaces(formik.values.email).toLowerCase();

            // make api call to resend otp
            const resendOtpResult = await dispatch(resendOtp(cleanEmail));

            // if resend otp api status is true then set seconds to 60 and set submitting to false
            if (resendOtpResult?.payload?.status) {
                setSeconds(60);
                setResendLoading(false);
                formik.setSubmitting(false);
                return;
            }

            // if resend otp api is rejected then set error message in email input field
            formik.setFieldError('otp', resendOtpResult.payload);
            formik.setSubmitting(false);
            formik.setFieldTouched('otp', true, false);
            setResendLoading(false);
        } catch (error) {
            // handle error part
            formik.setFieldError('email', 'Failed to resend magic link. Please try again.');
            formik.setSubmitting(false);
            formik.setFieldTouched('email', true, false);
            setResendLoading(false);
        }
    }

    async function handlePasswordLogin() {
        try {
            // set otp login false in login with password method
            formik.setFieldValue('isOtpLogin', false);

            // set error if email is not provided
            if (!formik.values.email) {
                formik.setFieldError('email', 'Email is required');
                formik.setSubmitting(false);
                return;
            }

            // clean email and convert to lowercase
            const cleanEmail = trimAndCleanSpaces(formik.values.email).toLowerCase();

            // check the email is exists or not in database
            const emailCheckResult = await dispatch(checkEmailExists(cleanEmail));

            // if email check result api is rejected then set error message in email input field
            if (emailCheckResult?.error?.message === 'Rejected') {
                formik.setFieldError('email', 'Something went wrong! Please try again.');
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
                return;
            }

            // if email does not exist then set error message in email input field
            if (!emailCheckResult?.payload?.exist) {
                formik.setFieldError('email', 'Email does not exist. Please sign up.');
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
                return;
            }

            // if email exists then set active login method to passwordLogin and update the email state globally
            if (emailCheckResult?.payload?.exist) {
                setActiveLoginMethod('passwordLogin');
                dispatch(setEmail(cleanEmail));
                return;
            }
        } catch (error) {
            // handle error part
            if (error.message && error.message.includes('checkEmailExists')) {
                formik.setSubmitting(false);
            } else {
                formik.setFieldError('email', 'Failed to send magic link. Please try again.');
                formik.setSubmitting(false);
                formik.setFieldTouched('email', true, false);
            }
        }
    }

    async function signInWithPassword() {
        try {
            // set password field error if password not provided
            if (!formik.values.password) {
                formik.setFieldError('password', 'Password is required');
                return;
            }

            // make a payload object with email and password
            const payload = {
                email: trimAndCleanSpaces(formik.values.email).toLowerCase(),
                password: formik.values.password
            };

            // make api call to login with email and password
            const loginWithPassword = await dispatch(loginWithEmailAndPassword(payload));
            // console.log("Response : ", loginWithPassword);

            // if (loginWithPassword?.payload) {
            //     formik.setErrors({ password: loginWithPassword.payload });
            //     formik.setSubmitting(false);
            //     formik.setFieldTouched("password", true, false);
            // }

            // if loginWithPassword api is rejected then set error message in password input field
            if (loginWithPassword?.error?.message === 'Rejected') {
                formik.setFieldError('password', loginWithPassword.payload);
                formik.setSubmitting(false);
                formik.setFieldTouched('password', true, false);
                return;
            }

            formik.resetForm(); // reset the form after successful login
        } catch (error) {
            // handle error part
            formik.setFieldError('password', 'Failed to login. Please try again.');
            formik.setSubmitting(false);
            formik.setFieldTouched('password', true, false);
        }
    }

    function backToOptions() {
        formik.setFieldValue('isOtpLogin', true);
        setActiveLoginMethod('login');
    }

    function getSlideClass(method) {
        if (activeLoginMethod === method) return 'slide-active';

        if (
            (activeLoginMethod === 'otpLogin' && method === 'login') ||
            (activeLoginMethod === 'passwordLogin' && method === 'login')
        )
            return 'slide-left';

        if (activeLoginMethod === 'login' && method !== 'login') return 'slide-right';

        return 'slide-hidden';
    }

    // to show login sub messages conditionally based on the active login method
    function getLoginMessage() {
        if (activeLoginMethod === 'forgotPassword') {
            return 'Enter your email to reset your password';
        }

        if (activeLoginMethod === 'passwordLogin' || activeLoginMethod === 'otpLogin') {
            return `Sign in as ${formik?.values?.email || ''}`;
        }

        return "We're happy to see you again.";
    }

    return {
        formik,
        showPassword,
        setShowPassword,
        activeLoginMethod,
        handleOtpLogin,
        handlePasswordLogin,
        backToOptions,
        getSlideClass,
        signInWithPassword,
        resendOtpEmail,
        resendLoading,
        seconds,
        loginWithOtp,
        getLoginMessage,
        isTooManyRequests
    };
}

const initialValues = {
    email: '',
    password: '',
    otp: '',
    isOtpLogin: false
};

const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email format').required('Email is required'),
    password: Yup.string().when('isOtpLogin', {
        is: false,
        then: (schema) => schema.required('Password is required'),
        otherwise: (schema) => schema.notRequired()
    }),
    otp: Yup.string().when('isOtpLogin', {
        is: true,
        then: (schema) => schema.required('OTP is required'),
        otherwise: (schema) => schema.notRequired()
    }),
    isOtpLogin: Yup.boolean()
});
